import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Backdrop, Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import ReportFooter from '../shared/components/ReportFooter';
import Footer from '../../general/Footer';
import { InvoiceInProgressProvider } from '../shared/context/InvoiceProgressContext';
import { MagicReportProvider, useMagicReport } from './context/MagicReportContext';
import { useAppContext } from 'common/AppContext';
import {
  closeMagicReportRevision,
  completeInitialSync,
  createMagicReportCheckpoint,
  deleteMagicReport,
  getMagicReport,
} from './magic-report.api';
import { ReductionCommentsSection } from './sections/ReductionCommentsSection';
import { setActiveStep as setVersionActiveStep } from './magic-report-version';
import VersionHistoryView from './history/VersionHistoryView';
import { VERSION_HISTORY_ENABLED } from './history/version-history.config';
import { getBudgetandLineItems } from '../../project/project.api';
import { useAuthorInfo } from '../shared/hooks/useAuthorInfo';
import { GenericWizardStepper, useWizardNavigation, useStepCompletionStatus, type StepCompletionStatus } from '../shared/wizard';
import { Button } from 'components/index';
import { toast } from 'react-toastify';
import { usePrompt } from '../../../components/AppPrompt';
import {
  COVER_LETTER_AMOUNTS_SUBSTEP,
  RECAP_OF_PREVIOUS_REPORTS_SUBSTEP,
  STATIC_SECTION_IDS,
  SUBSTEP_ID_TO_SECTION_KEY,
} from './sections/section-utils';
import { getSectionRank } from './sections/action-items.util';
import {
  DOCUMENT_REVIEW_STEP_INDEX,
  MAGIC_REPORT_BASE_STEPS,
  MAGIC_REPORT_SCROLL_SPY_STEP_IDS,
  PAYAPP_REVIEW_STEP_INDEX,
  REVIEW_BUDGET_STEP_INDEX,
  type WizardStepConfig,
} from './magic-report-wizard.config';
import { DrawContext, getPrimaryDraw } from './magic-report.type';
import { parseDrawScopedKey, stripDrawScope } from './sections/draw-scoped-sections.util';
import { mapReportSectionsToState, normalizeReportSummary } from './magic-report-state.util';
import { buildReviewBudgetSubSteps } from './sections/section-utils';
import { getMissingRequiredFields } from '../../project/project.util';
import { ProjectSummary } from '../../project/project.type';
import RequiredFieldsGateDialog from '../../project/RequiredFieldsGateDialog';
import { ReviewBudgetDrawTabProvider, useReviewBudgetDrawTab } from '../shared/steps/budget/ReviewBudgetDrawTabContext';

// Using shared InvoiceInProgressProvider

const REVIEW_BUDGET_SECTION_KEYS: Record<string, string> = {
  'review-budget-application-comments': 'application-comments',
  'review-budget-reduction-comments': 'reduction-comments',
};

/** Review Budget substeps that must be complete (for every draw) before the step counts as done. */
const REVIEW_BUDGET_GATING_SUBSTEP_IDS = ['review-budget-application-comments', 'review-budget-reduction-comments'];

const MagicReportWizardContent = () => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [isStepperCollapsed, setIsStepperCollapsed] = useState(false);
  const { setPageHeaderText, setBreadcrumbProjectLabel } = useAppContext();
  const location = useLocation();
  const { reportData } = location.state || {};
  const navigate = useNavigate();
  const { projectId, reportId, stepId } = useParams<{ projectId: string; reportId: string; stepId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isReadOnlyView = searchParams.get('readOnly') === 'true';
  const isHistoryOpen = searchParams.get('history') === '1';
  // History lives in the URL so the browser's Back button closes it.
  const setHistoryOpen = (open: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (open) next.set('history', '1');
    else next.delete('history');
    setSearchParams(next, { state: location.state });
  };
  const { getConfirmation } = usePrompt();

  const {
    reportState,
    completionStatusVersion,
    savedVersion,
    photos,
    photosLoaded,
    saveReport,
    isDirty,
    isSaving,
    refreshCompletionStatus,
    runAllSectionSyncs,
    hasSectionSyncs,
    uncompleteSections,
    setReportState,
    setIsDirty,
    setCurrentStep,
    hasUnsavedPhotoChanges,
    navigationGuardRef,
    discardChanges,
  } = useMagicReport();

  const { isLoading: isLoadingProfile } = useAuthorInfo();
  const { activeDrawId, setActiveDrawId } = useReviewBudgetDrawTab() ?? { activeDrawId: null, setActiveDrawId: () => undefined };

  useEffect(() => {
    const reportName = reportData?.report?.name || reportState.reportName || '';
    setPageHeaderText(isReadOnlyView && reportName ? `Photos- ${reportName}` : reportName);

    const projectSummary = reportData?.projectSummary || reportState.projectSummary;
    const projectLabel =
      projectSummary?.projectNumber && projectSummary?.projectTitle
        ? `${projectSummary.projectNumber} ${projectSummary.projectTitle}`
        : projectSummary?.projectTitle;
    setBreadcrumbProjectLabel(projectLabel);

    return () => {
      setPageHeaderText('');
      setBreadcrumbProjectLabel(undefined);
    };
  }, [
    reportData?.report?.name,
    reportState.reportName,
    isReadOnlyView,
    reportData?.projectSummary,
    reportState.projectSummary,
    setPageHeaderText,
    setBreadcrumbProjectLabel,
  ]);

  // Use shared step completion status hook
  const stepCompletionStatus = useStepCompletionStatus({
    photos,
    photosLoaded,
    budgetSummary: reportState.budgetSummary,
    invoices: reportState.invoices,
    completionStatusVersion,
    invoiceStepCompleted: Boolean((reportState as any).invoiceData?.isCompleted),
  });

  // Build dynamic substeps for Payapp Review and Document Review (custom sections interleaved with static substeps)
  const steps = useMemo(() => {
    const mergeDynamicSubsteps = (staticSubSteps: WizardStepConfig['subSteps'] = []) => {
      const orderedSubSteps: NonNullable<WizardStepConfig['subSteps']> = [];

      const dynamicSections = (reportState.sections as any)?.dynamicSections as
        | Array<{ fields: { fieldName: string; value?: string }[]; insertAfterSectionId?: string }>
        | undefined;

      const groupedDynamicSections = new Map<string, Array<{ section: any; originalIndex: number }>>();
      if (dynamicSections) {
        dynamicSections.forEach((section, idx) => {
          const key = section.insertAfterSectionId || 'end';
          if (!groupedDynamicSections.has(key)) {
            groupedDynamicSections.set(key, []);
          }
          const group = groupedDynamicSections.get(key);
          if (group) {
            group.push({ section, originalIndex: idx });
          }
        });
      }

      const startDynamics = groupedDynamicSections.get('start') || [];
      startDynamics.forEach(({ section, originalIndex }) => {
        const headerField = section.fields.find((f: any) => f.fieldName === 'header');
        const title = (headerField?.value || `Custom Section ${originalIndex + 1}`).toString();
        orderedSubSteps.push({
          id: `dynamic-section-${originalIndex}`,
          title,
        });
      });

      const isReviewingConstructionMeetingMinutes = reportState.projectSummary?.reviewingConstructionMeetingMinutes === true;
      const isReviewingPotentialChangeOrders = reportState.projectSummary?.reviewingPotentialChangeOrders === true;

      staticSubSteps.forEach(staticSubStep => {
        if (staticSubStep.id === 'construction-meeting-minutes' && !isReviewingConstructionMeetingMinutes) {
          return;
        }
        if (staticSubStep.id === 'potential-change-orders' && !isReviewingPotentialChangeOrders) {
          return;
        }
        orderedSubSteps.push(staticSubStep);
        const dynamicsForThisPosition = groupedDynamicSections.get(staticSubStep.id) || [];
        dynamicsForThisPosition.forEach(({ section, originalIndex }) => {
          const headerField = section.fields.find((f: any) => f.fieldName === 'header');
          const title = (headerField?.value || `Custom Section ${originalIndex + 1}`).toString();
          orderedSubSteps.push({
            id: `dynamic-section-${originalIndex}`,
            title,
          });
        });
      });

      const endDynamics = groupedDynamicSections.get('end') || [];
      endDynamics.forEach(({ section, originalIndex }) => {
        const headerField = section.fields.find((f: any) => f.fieldName === 'header');
        const title = (headerField?.value || `Custom Section ${originalIndex + 1}`).toString();
        orderedSubSteps.push({
          id: `dynamic-section-${originalIndex}`,
          title,
        });
      });

      return orderedSubSteps;
    };

    return MAGIC_REPORT_BASE_STEPS.map(step => {
      if (step.id === 'review-budget') {
        return { ...step, subSteps: buildReviewBudgetSubSteps(reportState.draws || []) };
      }
      if (step.id === 'payapp-review') {
        // "Amounts for Cover Letter" always sits at the very top of Payapp Review,
        // ahead of any dynamic sections (even ones inserted at 'start').
        return { ...step, subSteps: [COVER_LETTER_AMOUNTS_SUBSTEP, ...mergeDynamicSubsteps(step.subSteps)] };
      }
      if (step.id === 'document-review') {
        // The recap editor always sits at the very bottom, after any dynamic sections.
        return { ...step, subSteps: [...mergeDynamicSubsteps(step.subSteps), RECAP_OF_PREVIOUS_REPORTS_SUBSTEP] };
      }
      return step;
    });
  }, [
    reportState.sections,
    reportState.draws,
    reportState.projectSummary?.reviewingConstructionMeetingMinutes,
    reportState.projectSummary?.reviewingPotentialChangeOrders,
  ]);

  const resolveSectionKey = React.useCallback((subStepId: string): string => {
    // Review Budget substeps are suffixed with their draw id. Resolve on the unsuffixed id, then
    // re-apply the suffix so we land on that draw's own section key.
    const drawScope = parseDrawScopedKey(subStepId);
    const baseSubStepId = drawScope?.baseKey ?? subStepId;
    const drawSuffix = drawScope ? `__${drawScope.drawId}` : '';

    const reviewBudgetKey = REVIEW_BUDGET_SECTION_KEYS[baseSubStepId];
    if (reviewBudgetKey) return `${reviewBudgetKey}${drawSuffix}`;

    let sectionKey: string = SUBSTEP_ID_TO_SECTION_KEY[baseSubStepId as keyof typeof SUBSTEP_ID_TO_SECTION_KEY];
    if (!sectionKey) {
      const dynMatch = subStepId.match(/^dynamic-section-(\d+)$/);
      sectionKey = dynMatch ? `dynamicSections[${dynMatch[1]}]` : subStepId;
    }
    return sectionKey;
  }, []);

  const subStepRankFn = useMemo(
    () => (subStepId: string) => getSectionRank(reportState.sections, resolveSectionKey(subStepId)),
    [reportState.sections, resolveSectionKey],
  );

  const coverPage = (reportState as any).coverPage;

  const subStepCompletedFn = useMemo(
    () => (subStepId: string) => {
      if (subStepId === 'review-budget-cover-letter-amounts') {
        return Boolean(coverPage?.isCompleted);
      }
      // Draw group headers are labels, not completable work.
      if (subStepId.startsWith('review-budget-draw-header')) return false;
      const sectionKey = resolveSectionKey(subStepId);
      const dynMatch = sectionKey.match(/^dynamicSections\[(\d+)\]$/);
      const section = dynMatch
        ? (reportState.sections as any)?.dynamicSections?.[Number(dynMatch[1])]
        : (reportState.sections as any)?.[sectionKey];
      return Boolean(section?.isCompleted);
    },
    [reportState.sections, coverPage, resolveSectionKey],
  );

  // Map Magic Report step indices to completion status indices
  // Steps: 0 Photos, 1 Review Budget, 2 Payapp Review, 3 Document Review, 4 Invoice, 5 Preview
  // Completion status: 0 Photos, 1 Review Budget, 2 Add Notes (limited)/unused, 3 Invoice, 4 Preview
  const getCompletionStatusForStep = React.useCallback(
    (stepIndex: number): StepCompletionStatus => {
      if (stepIndex === PAYAPP_REVIEW_STEP_INDEX || stepIndex === DOCUMENT_REVIEW_STEP_INDEX) {
        // The recap editor has no completion toggle, so it doesn't gate the step.
        const subSteps = (steps[stepIndex]?.subSteps || []).filter(subStep => subStep.id !== RECAP_OF_PREVIOUS_REPORTS_SUBSTEP.id);
        const isComplete = subSteps.length > 0 && subSteps.every(subStep => subStepCompletedFn(subStep.id));
        return { isComplete, hasWarning: false };
      }

      const statusMap: { [key: number]: number } = {
        0: 0,
        4: 3,
        5: 4,
      };

      if (stepIndex === 1) {
        // Complete only when every covered draw's gating substeps are complete.
        const gatingSubStepIds = (steps[stepIndex]?.subSteps || [])
          .filter(subStep => REVIEW_BUDGET_GATING_SUBSTEP_IDS.includes(stripDrawScope(subStep.id)))
          .map(subStep => subStep.id);
        const idsToCheck = gatingSubStepIds.length > 0 ? gatingSubStepIds : REVIEW_BUDGET_GATING_SUBSTEP_IDS;
        const isComplete = idsToCheck.every(id => subStepCompletedFn(id));
        return { isComplete, hasWarning: false };
      }

      const statusIndex = statusMap[stepIndex];
      return statusIndex !== undefined ? stepCompletionStatus[statusIndex] : { isComplete: false, hasWarning: false };
    },
    [steps, subStepCompletedFn, stepCompletionStatus],
  );

  // Create mapped stepCompletionStatus array aligned with Magic Report step indices
  const mappedStepCompletionStatus = useMemo(
    () => steps.map((_, index) => getCompletionStatusForStep(index)),
    [steps, getCompletionStatusForStep],
  );

  const excludeFromOverallProgress = React.useCallback(
    (stepIndex: number, subStepId?: string) =>
      (!!subStepId && (stripDrawScope(subStepId) === 'review-budget-g702-3' || subStepId === RECAP_OF_PREVIOUS_REPORTS_SUBSTEP.id)) ||
      steps[stepIndex]?.id === 'preview',
    [steps],
  );

  const versionedReportId = reportData?.report?.id || '';
  // History is off in some environments (VERSION_HISTORY_ENABLED); its link, view and calls are skipped then.
  const historyReportId = VERSION_HISTORY_ENABLED ? versionedReportId : '';
  const stepKeys = useMemo(() => steps.map(step => step.id), [steps]);
  // Leaving a main step without saving still ends its revision in the version history.
  const closeStepRevision = React.useCallback(() => {
    if (!historyReportId) return;
    closeMagicReportRevision(historyReportId).catch(error => console.error('Failed to close report revision', error));
  }, [historyReportId]);

  // Use shared wizard navigation hook
  const { activeStep, setActiveStep, handleNext, handleBack, handleStepClick, handleDelete } = useWizardNavigation({
    totalSteps: steps.length,
    saveReport,
    isDirty,
    hasUnsavedPhotoChanges,
    isSaving,
    refreshCompletionStatus,
    deleteReport: async (reportId: string) => {
      await deleteMagicReport(reportId);
      discardChanges();
    },
    reportName: reportData?.report?.name || '',
    reportId: reportData?.report?.id || '',
    sectionsStepIndices: [PAYAPP_REVIEW_STEP_INDEX, DOCUMENT_REVIEW_STEP_INDEX],
    projectId,
    navigationGuardRef,
    stepKeys,
    onLeaveStep: closeStepRevision,
  });

  // Save pending edits before opening the history, so the latest work is in it and nothing is left
  // to save in the background while the user browses. If the save fails, the history view still
  // opens and shows its "unsaved changes" warning.
  const isOpeningHistoryRef = useRef(false);
  const openHistory = async () => {
    if (isOpeningHistoryRef.current) return;
    isOpeningHistoryRef.current = true;
    try {
      if (isDirty || hasUnsavedPhotoChanges) await saveReport({ silent: true, reason: 'manual' });
    } finally {
      isOpeningHistoryRef.current = false;
      setHistoryOpen(true);
    }
  };

  // Saves that don't name a step (Save Draft, autosave) are recorded against the step on screen.
  useEffect(() => {
    if (versionedReportId) setVersionActiveStep(versionedReportId, stepKeys[activeStep]);
  }, [versionedReportId, stepKeys, activeStep]);

  const updateUrlForStep = React.useCallback(
    (stepIndex: number, replace = false) => {
      if (!projectId || !reportId) return;
      const step = steps[stepIndex];
      if (!step) return;
      const path = `/projects/${projectId}/magic-report/${reportId}/${step.id}`;
      navigate(path, { replace, state: location.state });
    },
    [navigate, projectId, reportId, steps, location.state],
  );

  // Keep a ref to the latest activeStep so the sync effect can read it without
  // listing it as a dependency (which would cause URL↔state loops on each step change).
  const activeStepRef = useRef(activeStep);
  activeStepRef.current = activeStep;

  // Sync URL stepId → activeStep for browser back/forward navigation only.
  // Do NOT add activeStep to deps: the effect must only react to URL changes,
  // not to programmatic step changes (which would cause the flicker race condition).
  useEffect(() => {
    if (!stepId) {
      updateUrlForStep(0, true);
      return;
    }
    const indexFromUrl = steps.findIndex(step => step.id === stepId);
    if (indexFromUrl < 0) {
      updateUrlForStep(PAYAPP_REVIEW_STEP_INDEX, true);
      return;
    }
    if (indexFromUrl >= 0 && indexFromUrl !== activeStepRef.current) {
      setActiveStep(indexFromUrl);
    }
  }, [stepId, steps, setActiveStep, updateUrlForStep]);

  const handleNextWithUrl = async () => {
    const nextIndex = Math.min(activeStep + 1, steps.length - 1);
    const didTransition = await handleNext();
    if (didTransition) updateUrlForStep(nextIndex);
  };

  const handleBackWithUrl = async () => {
    const prevIndex = Math.max(activeStep - 1, 0);
    const didTransition = await handleBack();
    if (didTransition) updateUrlForStep(prevIndex);
  };

  const handleStepClickWithUrl = async (index: number) => {
    const didTransition = await handleStepClick(index);
    if (didTransition) updateUrlForStep(index);
  };

  // Sync activeStep with context for auto-save control
  useEffect(() => {
    setCurrentStep(activeStep);
  }, [activeStep, setCurrentStep]);

  const scrollToSubStep = React.useCallback((subStepId: string) => {
    const tryScroll = (attempt: number) => {
      const container = contentRef.current;
      if (!container) return;
      const el = document.getElementById(subStepId);
      if (!el || !container.contains(el)) {
        if (attempt < 20) {
          window.requestAnimationFrame(() => tryScroll(attempt + 1));
        }
        return;
      }
      const containerTop = container.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      const top = elTop - containerTop + container.scrollTop - 8;
      container.scrollTo({ top, behavior: 'smooth' });
    };
    tryScroll(0);
  }, []);

  const scrollToTop = React.useCallback(() => {
    const container = contentRef.current;
    if (!container) return;
    container.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Track active subsection while scrolling (Payapp / Document review + Review Budget anchors).
  useEffect(() => {
    const wizardStepId = steps[activeStep]?.id;
    if (!wizardStepId || !MAGIC_REPORT_SCROLL_SPY_STEP_IDS.has(wizardStepId)) {
      setActiveSectionId(null);
      return;
    }

    const container = contentRef.current;
    if (!container) return;

    let sectionElements: Element[];
    if (wizardStepId === 'review-budget') {
      sectionElements = Array.from(container.querySelectorAll('[id^="review-budget-"]'));
    } else {
      const staticSectionIds = STATIC_SECTION_IDS as readonly string[];
      sectionElements = Array.from(container.querySelectorAll('[id]')).filter(el => {
        const id = el.id;
        return (
          (staticSectionIds as string[]).includes(id) ||
          id.startsWith('dynamic-section-') ||
          id === 'review-budget-cover-letter-amounts' ||
          id === RECAP_OF_PREVIOUS_REPORTS_SUBSTEP.id
        );
      });
    }

    if (sectionElements.length === 0) return;

    const observerOptions = {
      root: container,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver(entries => {
      let mostVisibleId: string | null = null;
      let maxRatio = 0;

      for (const entry of entries) {
        if (entry.isIntersecting && entry.target instanceof HTMLElement) {
          const ratio = entry.intersectionRatio;
          const targetId = entry.target.id;
          if (ratio > maxRatio && targetId) {
            maxRatio = ratio;
            mostVisibleId = targetId;
          }
        }
      }

      if (mostVisibleId) {
        setActiveSectionId(mostVisibleId);
      }
    }, observerOptions);

    sectionElements.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
    };
    // activeDrawId: switching the Review Budget draw tab swaps the whole set of anchor elements,
    // so the observer has to be rebuilt against the newly mounted panel.
  }, [activeStep, reportState.sections, steps, activeDrawId]);

  const handleRunThisMonth = async () => {
    const isConfirmed = await getConfirmation({
      heading: 'Run this Month',
      description: `This will update the following sections with this month's data:
        <ul>
          <li><b>Reallocations</b></li>
          <li><b>Change Orders</b></li>
          <li><b>Stored Materials</b></li>
          <li><b>Retainage</b></li>
          <li><b>Minor Adjustments</b></li>
        </ul>
        Are you sure you want to continue?`,
      confirmButton: {
        label: 'Continue',
        color: 'primary',
      },
      cancelButton: {
        label: 'Cancel',
        color: 'secondary',
      },
    });

    if (!isConfirmed) return;
    await runThisMonthSyncs();
  };

  const runThisMonthSyncs = async () => {
    // Mark the state just before the bulk re-sync, so the user can go back to it.
    if (historyReportId) {
      try {
        await createMagicReportCheckpoint(historyReportId, 'RunThisMonth');
      } catch (error) {
        console.error('Failed to record version before Run this Month', error);
      }
    }

    try {
      await runAllSectionSyncs();
      // The modules now hold new numbers, so they need reviewing again.
      const payappSectionKeys = (steps[PAYAPP_REVIEW_STEP_INDEX]?.subSteps || []).map(subStep => resolveSectionKey(subStep.id));
      uncompleteSections(payappSectionKeys);
      setReportState((prev: any) => ({
        ...prev,
        coverPage: prev.coverPage ? { ...prev.coverPage, isCompleted: false } : prev.coverPage,
        sections: Object.fromEntries(
          Object.entries(prev.sections || {}).map(([key, section]: [string, any]) =>
            payappSectionKeys.includes(key) && section?.isCompleted ? [key, { ...section, isCompleted: false }] : [key, section],
          ),
        ),
      }));
      setIsDirty(true);
      toast.success('All sections updated successfully!');
    } catch (error) {
      console.error('Error running section syncs:', error);
      toast.error('Failed to update sections. Please try again.');
    }
  };

  // The user already said yes to "Run this Month" when opening the report (first open after Start
  // Next), so run it once, without asking again, as soon as the Payapp Review sections are ready.
  const autoRunThisMonthRef = useRef(Boolean((location.state as any)?.runThisMonth));
  // Called from timers, so read the latest closure (versionedReportId may arrive after mount).
  const runThisMonthSyncsRef = useRef(runThisMonthSyncs);
  runThisMonthSyncsRef.current = runThisMonthSyncs;

  useEffect(() => {
    if (!autoRunThisMonthRef.current || activeStep !== PAYAPP_REVIEW_STEP_INDEX) return;
    autoRunThisMonthRef.current = false;
    // Drop the flag from history state, which every step change and a page refresh carry forward.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: { ...(location.state as any), runThisMonth: false } });

    // Sections register their syncs on mount, some only once their data has loaded. Wait for the
    // first one, then give the rest of the step a moment to register before running them all.
    let cancelled = false;
    const startedAt = Date.now();
    const waitForSections = () => {
      if (cancelled) return;
      if (hasSectionSyncs()) {
        setTimeout(() => {
          if (!cancelled) runThisMonthSyncsRef.current();
        }, 500);
        return;
      }
      if (Date.now() - startedAt > 10000) {
        toast.error('Could not run this month automatically. Use the Run this Month button.');
        return;
      }
      setTimeout(waitForSections, 250);
    };
    waitForSections();

    return () => {
      cancelled = true;
    };
  }, [activeStep]);

  // First open after Start Next: the budget-driven sections live on other steps, so they are mounted
  // hidden, synced and saved once, then never sync on their own again this period.
  const [initialSyncRunning, setInitialSyncRunning] = useState(Boolean(reportState.initialSyncPending));
  const completeAfterSaveRef = useRef(false);

  const markInitialSyncDone = async () => {
    if (reportState.reportId) await completeInitialSync(reportState.reportId);
    setReportState((prev: any) => ({ ...prev, initialSyncPending: false }));
  };

  useEffect(() => {
    if (!completeAfterSaveRef.current) return;
    completeAfterSaveRef.current = false;
    markInitialSyncDone().catch(error => console.error('Initial sync complete failed', error));
  }, [savedVersion]);

  useEffect(() => {
    if (!initialSyncRunning) return;
    let cancelled = false;
    const startedAt = Date.now();
    const finish = async () => {
      try {
        await runAllSectionSyncs();
        // Sections hand their changes to the report state on their next render; saving before that
        // would save the old data, and unmounting the hidden sections would then drop the changes.
        await new Promise(resolve => setTimeout(resolve, 300));
        // Left mid-way: the hidden sections dropped their changes, so keep the sync pending.
        if (cancelled) return;
        if (!(await saveReport())) {
          // The synced rows are in the report state; the next successful save completes the sync.
          completeAfterSaveRef.current = true;
          return;
        }
        await markInitialSyncDone();
      } catch (error) {
        console.error('Initial sync failed', error);
        toast.error('Could not sync sections from the budget. Reopen the report to try again.');
      } finally {
        if (!cancelled) setInitialSyncRunning(false);
      }
    };
    const waitForSections = () => {
      if (cancelled) return;
      if (hasSectionSyncs()) {
        setTimeout(() => {
          if (!cancelled) finish();
        }, 500);
        return;
      }
      if (Date.now() - startedAt > 10000) {
        setInitialSyncRunning(false);
        toast.error('Could not sync sections from the budget. Reopen the report to try again.');
        return;
      }
      setTimeout(waitForSections, 250);
    };
    waitForSections();

    return () => {
      cancelled = true;
    };
  }, [initialSyncRunning]);

  if (isLoadingProfile) return <Typography>Loading...</Typography>;
  // Falls back to context state (always populated by the time this component mounts,
  // since the parent MagicReportWizard fetches by reportId) rather than location.state,
  // which is empty on a fresh page load/new tab (no router navigation history).
  if (!reportState?.reportId)
    return <Typography>Error: Report data not found! Please go back to the project page and open the report again.</Typography>;

  const getViewOnlyLinkForStep = (id: string) => {
    if (id !== 'upload-images' || !projectId || !reportId) return undefined;
    return `/projects/${projectId}/magic-report/${reportId}/upload-images?readOnly=true`;
  };

  if (isReadOnlyView) {
    const photosComponent = steps.find(step => step.id === 'upload-images')?.component;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
          {React.isValidElement<{ isReadOnly?: boolean }>(photosComponent)
            ? React.cloneElement(photosComponent, { isReadOnly: true })
            : null}
        </Box>
        <Footer>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, flex: 1, color: 'text.secondary' }}>
            <VisibilityIcon fontSize='small' />
            <Typography variant='body2' sx={{ fontWeight: 500 }}>
              View only
            </Typography>
          </Box>
        </Footer>
      </Box>
    );
  }

  // Render content directly from STEPS so indices stay aligned with the left stepper

  return (
    <InvoiceInProgressProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
          <Box
            sx={{
              width: isStepperCollapsed ? '64px' : '275px',
              flexShrink: 0,
              p: 2,
              borderRight: theme => `1px solid ${theme.palette.divider}`,
              overflowY: 'auto',
              overflowX: 'hidden',
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              transition: 'width 0.2s ease',
            }}
          >
            <GenericWizardStepper
              steps={steps}
              activeStep={activeStep}
              isSaving={isSaving}
              activeSubStepId={activeSectionId}
              isScrollSpyStep={stepId => MAGIC_REPORT_SCROLL_SPY_STEP_IDS.has(stepId)}
              getCompletionStatusForStep={getCompletionStatusForStep}
              getSubStepRank={subStepRankFn}
              getSubStepCompleted={subStepCompletedFn}
              excludeFromOverallProgress={excludeFromOverallProgress}
              getViewOnlyLinkForStep={getViewOnlyLinkForStep}
              onStepClick={async index => {
                await handleStepClickWithUrl(index);
                scrollToTop();
              }}
              onSubStepClick={async (stepIndex, subStepId, isStepActive) => {
                if (!isStepActive) {
                  await handleStepClickWithUrl(stepIndex);
                }
                // Review Budget renders one tab per draw and mounts only the active one, so a
                // draw-scoped substep has to switch tabs first or its anchor won't be in the DOM.
                let didSwitchDraw = false;
                if (steps[stepIndex]?.id === 'review-budget') {
                  const drawId = parseDrawScopedKey(subStepId)?.drawId;
                  if (drawId && drawId !== activeDrawId) {
                    setActiveDrawId(drawId);
                    didSwitchDraw = true;
                  }
                }
                setTimeout(() => scrollToSubStep(subStepId), isStepActive && !didSwitchDraw ? 0 : 200);
              }}
              collapsed={isStepperCollapsed}
            />
          </Box>
          <Tooltip title={isStepperCollapsed ? 'Expand steps' : 'Collapse steps'} placement='right'>
            <IconButton
              onClick={() => setIsStepperCollapsed(prev => !prev)}
              size='small'
              sx={{
                position: 'absolute',
                top: '50%',
                left: isStepperCollapsed ? '64px' : '275px',
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
                bgcolor: 'background.paper',
                border: theme => `1px solid ${theme.palette.divider}`,
                color: 'text.secondary',
                boxShadow: 1,
                width: 24,
                height: 24,
                transition: 'left 0.2s ease',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {isStepperCollapsed ? <ChevronRightIcon sx={{ fontSize: 16 }} /> : <ChevronLeftIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <Box ref={contentRef} sx={{ flexGrow: 1, p: 3, overflowY: 'auto' }}>
            {steps[activeStep]?.component}
            {initialSyncRunning && (
              <Box sx={{ display: 'none' }} aria-hidden>
                {activeStep !== PAYAPP_REVIEW_STEP_INDEX && steps[PAYAPP_REVIEW_STEP_INDEX]?.component}
                {activeStep !== REVIEW_BUDGET_STEP_INDEX &&
                  reportState.reportId &&
                  (reportState.draws || []).map(draw => (
                    <ReductionCommentsSection key={draw.drawId} magicReportId={reportState.reportId ?? ''} drawId={draw.drawId} />
                  ))}
              </Box>
            )}
          </Box>
          <Backdrop
            open={initialSyncRunning}
            sx={{ zIndex: theme => theme.zIndex.modal + 1, color: '#fff', flexDirection: 'column', gap: 2 }}
          >
            <CircularProgress color='inherit' />
            <Typography color='inherit'>Syncing sections from the budget…</Typography>
          </Backdrop>
        </Box>
        <ReportFooter
          activeStep={activeStep}
          totalSteps={steps.length}
          onNext={handleNextWithUrl}
          onBack={handleBackWithUrl}
          onDelete={handleDelete}
          stepCompletionStatus={mappedStepCompletionStatus}
          stepTitles={steps.map(s => s.title)}
          invoiceStepIndex={4}
          onOpenHistory={historyReportId ? openHistory : undefined}
          customButton={
            activeStep === PAYAPP_REVIEW_STEP_INDEX ? (
              <Button variant='contained' color='secondary' onClick={handleRunThisMonth} disabled={isSaving}>
                Run this Month
              </Button>
            ) : null
          }
        />
      </Box>
      {isHistoryOpen && historyReportId && <VersionHistoryView reportId={historyReportId} onClose={() => setHistoryOpen(false)} />}
    </InvoiceInProgressProvider>
  );
};

const MagicReportWizard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { reportId } = useParams<{ reportId: string }>();
  const { reportData: locationReportData } = (location.state as any) || {};
  const { authorInfo, isLoading: isLoadingAuthor } = useAuthorInfo();
  const shouldFetchFullReport = !!reportId && reportId !== 'new';
  const [fixedProjectSummary, setFixedProjectSummary] = useState<ProjectSummary | null>(null);

  // Fetch report from backend only if not in location.state (e.g., on page refresh)
  const { data: fetchedReport, isLoading: isLoadingReport } = useQuery(
    ['magicReport', reportId],
    async () => {
      if (!reportId) throw new Error('Missing report ID');
      return getMagicReport(reportId, { trackVersion: true });
    },
    {
      enabled: shouldFetchFullReport,
      staleTime: 0, // Data becomes stale immediately
      cacheTime: 0, // Don't cache - always fetch fresh data
      refetchOnMount: 'always', // Always refetch when component mounts
      // The editor reads this report once, but each fetch updates the version it saves against.
      // A background refetch (e.g. on reconnect) would hand it a newer version without the newer
      // content, and its next save would overwrite someone else's work instead of being refused.
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  const locationDrawIds: string[] =
    locationReportData?.draws?.map((draw: DrawContext) => draw.drawId) ??
    (locationReportData?.drawSummary?.id ? [locationReportData.drawSummary.id] : []);
  const reportDrawIds: string[] = locationDrawIds.length > 0 ? locationDrawIds : fetchedReport?.drawIds || [];
  const projectIdForDrawFetch = fetchedReport?.project?.projectId;
  const shouldFetchDrawData = !!projectIdForDrawFetch && reportDrawIds.length > 0;

  const { data: fetchedDrawContexts, isLoading: isLoadingDraws } = useQuery(
    ['magicReportDrawData', projectIdForDrawFetch, reportDrawIds.join(',')],
    async () => {
      const drawData = await Promise.all(reportDrawIds.map(drawId => getBudgetandLineItems(projectIdForDrawFetch as string, drawId)));
      return drawData
        .filter(data => data?.projectDrawSummary)
        .map(data => ({
          drawId: data.projectDrawSummary.id,
          drawSummary: data.projectDrawSummary,
          budgetSummary: data.budgetSummary,
          lineItems: data.lineItems,
          subtotals: data.subtotals,
        }));
    },
    {
      enabled: shouldFetchDrawData,
      staleTime: 0,
      cacheTime: 0,
    },
  );

  // Use location state if available, otherwise construct from fetched report
  const reportData = useMemo(() => {
    if (fetchedReport) {
      const normalizedSummary = normalizeReportSummary(fetchedReport);
      // Merge project data: use locationReportData projectSummary if available (has complete data),
      // otherwise use fetchedReport.project, but merge in projectAddress from locationReportData if it exists
      let projectSummary = locationReportData?.projectSummary || fetchedReport.project;
      if (!projectSummary?.projectAddress && locationReportData?.projectSummary?.projectAddress && fetchedReport.project) {
        projectSummary = {
          ...fetchedReport.project,
          projectAddress: locationReportData.projectSummary.projectAddress,
        };
      }

      // The primary draw (last = latest application) mirrors into the legacy single-draw fields,
      // same convention as useMagicReportDrawSelection.confirmDrawSelection.
      // Fresh server data wins; router state is only a fallback if that fetch failed.
      const draws = fetchedDrawContexts ?? locationReportData?.draws;
      const primaryDraw = getPrimaryDraw(draws);

      return {
        report: normalizedSummary ? { ...fetchedReport, summary: normalizedSummary } : fetchedReport,
        projectSummary,
        draws,
        drawSummary: primaryDraw?.drawSummary ?? locationReportData?.drawSummary,
        budgetSummary: primaryDraw?.budgetSummary ?? locationReportData?.budgetSummary,
        lineItems: primaryDraw?.lineItems ?? locationReportData?.lineItems,
      };
    }

    if (locationReportData?.report) {
      const normalizedSummary = normalizeReportSummary(locationReportData.report);
      return {
        ...locationReportData,
        report: normalizedSummary ? { ...locationReportData.report, summary: normalizedSummary } : locationReportData.report,
      };
    }

    return locationReportData || null;
  }, [locationReportData, fetchedReport, fetchedDrawContexts]);

  const projectSummary = fixedProjectSummary || reportData?.projectSummary;
  const report = reportData?.report;

  const initialReportState = useMemo(() => {
    const coverPage = report?.coverPage || null;
    // Extract coverPagePhotoIds from coverPage if it exists, for backward compatibility
    const coverPagePhotoIds = coverPage?.coverPagePhotoIds || [];

    // Every draw this report covers, ordered oldest application first so the last entry is the
    // primary draw. Reports opened before multi-draw (and other legacy entry points) arrive with
    // only the single drawSummary/budgetSummary/lineItems trio, which becomes a one-draw list.
    const draws: DrawContext[] =
      (reportData?.draws as DrawContext[] | undefined) ??
      (reportData?.drawSummary
        ? [
            {
              drawId: reportData.drawSummary.id,
              drawSummary: reportData.drawSummary,
              budgetSummary: reportData.budgetSummary,
              lineItems: reportData.lineItems,
            },
          ]
        : []);

    const primaryDraw = draws.length > 0 ? draws[draws.length - 1] : undefined;
    // Legacy sections stored without a draw id bind to the first covered draw.
    const fallbackDrawId = draws[0]?.drawId;

    return {
      projectSummary: fixedProjectSummary || reportData?.projectSummary || fetchedReport?.project,
      draws,
      // Chosen in the draw-selection dialog at create/reset time and never written back from the
      // wizard, so it comes straight off the report on both the navigate and the refresh path.
      hiddenCoLienDrawIds: (report?.hiddenCoLienDrawIds as string[] | undefined) || [],
      initialSyncPending: Boolean((report as any)?.initialSyncPending),
      recapApplications: report?.recapApplications || [],
      // Mirrors of the primary draw. Sections not yet migrated to multi-draw read these; they go
      // away once every consumer reads `draws`.
      drawSummary: primaryDraw?.drawSummary ?? reportData?.drawSummary,
      budgetSummary: primaryDraw?.budgetSummary ?? reportData?.budgetSummary,
      lineItems: primaryDraw?.lineItems ?? reportData?.lineItems,
      summary: report?.summary,
      invoices: report?.invoices || [],
      author: authorInfo,
      reportName: report?.name || '',
      sections: mapReportSectionsToState(report?.sections, fallbackDrawId), // Map sections from fetched report
      previousFundingsComment: report?.previousFundingsNote || '', // Load previous fundings note
      coverPage: coverPage, // Load cover page data
      coverPagePhotoIds: coverPagePhotoIds, // Extract from coverPage
      siteConditions: report?.siteConditions || {}, // Load site conditions from saved report
      invoiceData: report?.invoiceData || {}, // Load invoice step completion data
      highlightedLineItemIds: report?.highlightedLineItemIds || [], // Load highlighted rows from saved report
    };
  }, [reportData, fetchedReport, report, authorInfo, fixedProjectSummary]);

  // The provider captures its initial state once on mount, so it must not mount before the draws arrive.
  if (isLoadingAuthor || isLoadingReport || isLoadingDraws) return <Typography>Loading...</Typography>;
  if (!report && !reportData)
    return <Typography>Error: Report data not found! Please go back to the project page and open the report again.</Typography>;

  const reportIdValue = report?.id || reportData?.report?.id || reportId;
  const projectId = projectSummary?.projectId || fetchedReport?.project?.projectId;

  if (!reportIdValue || !projectId) {
    return <Typography>Error: Missing report ID or project ID.</Typography>;
  }

  const missingRequiredFields = getMissingRequiredFields(projectSummary);
  if (missingRequiredFields.length > 0) {
    return (
      <RequiredFieldsGateDialog
        open
        projectId={projectId}
        missingFields={missingRequiredFields}
        projectSummary={projectSummary}
        onSaved={(updated: ProjectSummary) => setFixedProjectSummary(updated)}
        onCancel={() => navigate(-1)}
      />
    );
  }

  return (
    <InvoiceInProgressProvider>
      <MagicReportProvider initialData={initialReportState} projectId={projectId} magicReportId={reportIdValue}>
        <ReviewBudgetDrawTabProvider>
          <MagicReportWizardContent />
        </ReviewBudgetDrawTabProvider>
      </MagicReportProvider>
    </InvoiceInProgressProvider>
  );
};

export default MagicReportWizard;
